import React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, ArrowRight, CheckCircle2, Shield, Sparkles, BookOpen,
  Activity, Users, Calendar, FileText, Mic, ClipboardCheck, Target,
  Upload, BarChart3, Award, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SEO } from '../components/SEO';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { analytics } from '../hooks/useAnalytics';

const FEATURES = [
  {
    id: 'dashboard',
    title: 'Application Command Center',
    subtitle: 'See your entire application at a glance',
    description: 'A real-time readiness score tracks your progress across every section — Profile, Schools, Activities, Writing, Recommendations, and Documents. Know exactly where you stand and what to tackle next. Built to mirror the actual Common App workflow so nothing surprises you on submission day.',
    screenshot: '/screenshots/dashboard.png',
    icon: BarChart3,
    badge: 'Dashboard',
    highlights: ['Real-time readiness score (0-100%)', 'Section-by-section progress tracking', 'Quick-action links to every tool', 'Personalized next-step guidance'],
  },
  {
    id: 'activities',
    title: '10-Slot Activity Builder',
    subtitle: 'Mirrors the exact Common App format',
    description: 'Fill all 10 activity slots with the same fields admissions officers actually see: type, position, hours, grade levels, and 150-character descriptions. Drag-and-drop to reorder by importance. One click on "Optimize All" sends every description to our AI, which rewrites them with action verbs, quantified impact, and Ivy-caliber language.',
    screenshot: '/screenshots/activities.png',
    icon: Activity,
    badge: 'Activities',
    highlights: ['10 slots matching Common App exactly', 'Drag-and-drop priority reorder', 'AI batch optimization in one click', '150-character limit enforced automatically'],
  },
  {
    id: 'interview',
    title: 'AI Interview Coach',
    subtitle: 'Practice with the questions admissions officers actually ask',
    description: '19 curated questions across Behavioral, Academic, and Situational categories — each with strategy tips from experienced interviewers. Type your response, and our AI scores you 1-10 with specific feedback: strengths identified, improvements suggested, and a sample "excellent answer" personalized to your profile.',
    screenshot: '/screenshots/interview.png',
    icon: Mic,
    badge: 'Interview Prep',
    highlights: ['19 real interview questions across 3 categories', 'AI scoring with personalized feedback (1-10)', 'Strategy tips from experienced interviewers', 'Practice history tracks your improvement'],
  },
  {
    id: 'checklist',
    title: 'Submission Checklist',
    subtitle: 'A pre-flight report before you hit submit',
    description: '10 global checks flag what\'s required vs. recommended — personal statement, activities, recommenders, school list, documents. Per-school breakdowns show supplemental essay and deadline status for every college on your list. Export a printable PDF "Ready to Submit" report to share with parents or counselors.',
    screenshot: '/screenshots/checklist.png',
    icon: ClipboardCheck,
    badge: 'Checklist',
    highlights: ['10 global requirement checks', 'Per-school supplemental & deadline review', 'Required vs. Recommended priority flags', 'Export PDF "Ready to Submit" report'],
  },
  {
    id: 'timeline',
    title: 'Deadline Engine',
    subtitle: 'Never miss a deadline again',
    description: 'Deadlines auto-populate from every school on your list — Early Action, Early Decision, Regular Decision. Color-coded urgency indicators show what\'s due soon. Add custom milestones for recommendation requests, essay drafts, and campus visits. Everything synced, everything visible.',
    screenshot: '/screenshots/timeline.png',
    icon: Calendar,
    badge: 'Timeline',
    highlights: ['Auto-populated from your school list', 'Color-coded urgency indicators', 'Custom milestone tracking', 'Early Action / Regular Decision split'],
  },
];

const ADDITIONAL_TOOLS = [
  { icon: BookOpen, title: 'Essay Studio', desc: 'Write personal statements, supplementals, and additional info essays. AI coach provides structural feedback on thesis, narrative arc, and voice.' },
  { icon: FileText, title: 'Resume Builder', desc: 'AI generates a Harvard-style resume from your profile and activities. Export as a polished PDF for interviews and scholarship applications.' },
  { icon: Upload, title: 'Document Vault', desc: 'Upload transcripts, test scores, and report cards. AI reads your documents and auto-populates your profile — GPA, SAT, courses fill themselves in.' },
  { icon: Users, title: 'Recommendations Tracker', desc: 'Manage counselor, teacher, and other recommenders. Track FERPA waiver status and see who has submitted at a glance.' },
  { icon: Target, title: 'School Explorer', desc: '103+ school database with acceptance rates, rankings, and program details. Build a balanced Reach / Target / Safety list.' },
  { icon: GraduationCap, title: 'Profile Builder', desc: 'Four-section Common App profile (Basic, Family, Education, Academics). Every field maps directly to what admissions offices see.' },
];

export default function PortalPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="portal-page">
      <SEO
        title="Ivy League Admissions Portal — $99/yr Application Command Center"
        description="One managed system for the entire application: essays, deadlines, school list, recommenders, testing, extracurriculars, decisions, scholarships. Designed with insight from former admissions officers. $99/year."
        keywords="college admissions software, ivy league application platform, common app organizer, essay tracker, college deadlines tracker"
      />

      {/* Nav */}
      <MarketingNav />

      {/* Hero */}
      <section className="pt-16 bg-[#0F392B] text-white">
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/25 mb-6">
            <Award className="h-3.5 w-3.5 text-[#D4AF37]" />
            <span className="text-xs font-medium text-[#D4AF37] tracking-wide uppercase">Built with Ivy League Expertise</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            The Ivy League<br /><span className="italic text-[#D4AF37]">Admissions Portal.</span>
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed mb-8">
            A command center for the entire application — not a generic planning tool. Designed with direct insight from former admissions officers who evaluated thousands of files at Harvard, Yale, and Columbia, and refined by current Ivy League students who just navigated the process themselves.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/sign-up" onClick={() => analytics.ctaPlatformStart('platform-hero')}>
              <Button size="lg" className="bg-white text-[#0F392B] hover:bg-white/90 h-12 px-8 text-sm font-semibold shadow-xl" data-testid="platform-hero-cta">
                Start Your 7-Day Free Trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <p className="text-xs text-white/40 mt-4">No credit card required. Full access to every feature.</p>
        </div>
      </section>

      {/* Credibility Bar */}
      <section className="bg-[#F9F9F7] border-b border-[#E2E2E2] py-5">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-8 text-center">
            {[
              { value: '103+', label: 'Schools in database' },
              { value: '10', label: 'Activity slots (Common App format)' },
              { value: '19', label: 'Interview prep questions' },
              { value: 'AI', label: 'Essay & activity optimizer' },
              { value: 'PDF', label: 'Export resumes & checklists' },
            ].map((stat, i) => (
              <div key={i}>
                <span className="text-lg font-bold text-[#0F392B]">{stat.value}</span>
                <p className="text-[10px] text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chaos vs. System + Module Map */}
      <section className="py-16 bg-white" data-testid="portal-modules-section">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-14">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-medium mb-3">Why a System</p>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Most families run this process<br />on improvisation.
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                Essay drafts across five Google Docs. Deadlines in an email thread. A spreadsheet someone updates at midnight. Reminders that arrive after the fact.
              </p>
              <p className="text-sm text-foreground font-medium leading-relaxed">
                The Portal replaces all of it with one managed system — every module connected, every deadline tracked, every draft versioned, and a single readiness score that tells you exactly where you stand.
              </p>
            </div>
            <div className="bg-[#F9F9F7] border border-[#E2E2E2] rounded-2xl p-6">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-4 text-center">Everything the process requires, in one place</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {['Dashboard', 'Profile & Intake', 'School List', 'Essays & Documents', 'Applications & Deadlines', 'Recommenders', 'Testing', 'Extracurriculars', 'Decisions & Scholarships', 'Meetings & Action Plans', 'Interview Prep', 'Submission Checklist'].map((m, i) => (
                  <div key={i} className="bg-white border border-[#E2E2E2] rounded-lg px-3 py-2.5 text-center" data-testid={`portal-module-${i}`}>
                    <p className="text-[11px] font-medium text-[#0F392B]">{m}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Outcome Stats — Track Record */}
      <section className="bg-white py-10 border-b border-[#E2E2E2]" data-testid="portal-outcomes">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-medium text-center mb-5">Our Track Record</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { stat: '200+', sub: 'students placed at Ivy League schools' },
              { stat: '$38M+', sub: 'in scholarship dollars secured' },
              { stat: '~50', sub: 'clients/year — high-touch, never volume' },
              { stat: 'Harvard', sub: "ALM '17 — founder's pedigree" },
            ].map((s, i) => (
              <div key={`outcome-${i}`}>
                <p className="text-2xl md:text-3xl font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>{s.stat}</p>
                <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[#F9F9F7] py-14 border-b border-[#E2E2E2]" data-testid="portal-testimonials">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-medium mb-3">What Families Say</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Tested by the students who got in
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                quote: "The Optimize All button rewrote my activities in 60 seconds and I instantly saw the difference. Action verbs, real numbers — exactly the way my Harvard interviewer told me to write later.",
                name: "Priya S.",
                school: "Accepted, Harvard '29",
                role: "student",
              },
              {
                quote: "We tried a $15K consultant first — got a Notion template and a Calendly link. This portal gave my daughter actual structure: deadlines that auto-populate, a real readiness score, a checklist that catches what we'd have missed.",
                name: "Marcus W.",
                school: "Parent · daughter accepted Princeton",
                role: "parent",
              },
              {
                quote: "I refer families to ILC because the framework is the same one I'd write on a whiteboard. The 'Ivy Signals' rubric is the most honest articulation of what admissions readers actually weigh.",
                name: "Dana C.",
                school: "School counselor · 22 years experience",
                role: "counselor",
              },
            ].map((t, i) => (
              <Card key={`testimonial-${i}`} className="border-[#E2E2E2] bg-white hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="text-[#D4AF37] text-3xl leading-none mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>&ldquo;</div>
                  <p className="text-sm text-foreground/85 leading-relaxed italic mb-5">{t.quote}</p>
                  <div className="border-t border-[#E2E2E2] pt-4">
                    <p className="text-sm font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>{t.name}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t.school}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Showcases with Screenshots */}
      {FEATURES.map((feature, i) => (
        <section key={feature.id} className={`py-16 ${i % 2 === 1 ? 'bg-[#F9F9F7]' : 'bg-white'}`} data-testid={`feature-${feature.id}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
              {/* Text */}
              <div className={i % 2 === 1 ? 'lg:col-start-2' : ''}>
                <Badge className="bg-[#0F392B]/5 text-[#0F392B] border-[#0F392B]/10 text-xs mb-3">
                  <feature.icon className="h-3 w-3 mr-1" /> {feature.badge}
                </Badge>
                <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {feature.title}
                </h2>
                <p className="text-sm text-[#D4AF37] font-medium mb-3">{feature.subtitle}</p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">{feature.description}</p>
                <div className="space-y-2">
                  {feature.highlights.map((h, j) => (
                    <div key={j} className="flex items-start gap-2.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Screenshot */}
              <div className={i % 2 === 1 ? 'lg:col-start-1' : ''}>
                <div className="rounded-2xl overflow-hidden shadow-2xl border border-[#E2E2E2] bg-white">
                  <img src={feature.screenshot} alt={`${feature.title} screenshot`} className="w-full" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Additional Tools Grid */}
      <section className="py-16 bg-[#0F392B] text-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
              And That's Not All
            </h2>
            <p className="text-sm text-white/60 mt-2">Every tool a serious applicant needs, in one platform.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ADDITIONAL_TOOLS.map((tool, i) => (
              <div key={i} className="p-5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center mb-3">
                  <tool.icon className="h-4.5 w-4.5 text-[#D4AF37]" />
                </div>
                <h3 className="text-sm font-semibold mb-1">{tool.title}</h3>
                <p className="text-xs text-white/50 leading-relaxed">{tool.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Platform is Different */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20 text-xs mb-3">The Difference</Badge>
            <h2 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
              Why an Application System Designed by<br />Admissions Officers Matters
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: Shield,
                title: 'Built from the Inside',
                desc: 'Most college planning tools are built by software engineers who\'ve never read an application. Ours was designed with direct input from former admissions officers at Harvard, Yale, and Columbia — people who evaluated thousands of applicants and know exactly what makes a file stand out or get passed over.',
              },
              {
                icon: GraduationCap,
                title: 'Tested by Students Who Got In',
                desc: 'Current Ivy League students — the ones who just navigated this process successfully — tested every feature and provided feedback. The result is a system that feels intuitive because it was shaped by people who actually used the Common App under pressure.',
              },
              {
                icon: Sparkles,
                title: 'AI Trained on What Actually Works',
                desc: 'Our AI doesn\'t give generic advice. The essay coach, activity optimizer, and interview scorer are tuned to the patterns that admissions officers actually respond to — action verbs, quantified impact, distinctive narratives, and the specific language that signals a compelling candidate.',
              },
              {
                icon: Target,
                title: 'Mirrors the Real Workflow',
                desc: 'The portal\'s structure matches the actual Common App section by section — Profile, Activities (10 slots, 150-char limits), Writing (Personal + Supplementals), Recommendations, and more. When you complete your work here, transferring to the real application is seamless.',
              },
            ].map((item, i) => (
              <Card key={i} className="border-[#E2E2E2] hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-xl bg-[#0F392B]/5 flex items-center justify-center mb-4">
                    <item.icon className="h-5 w-5 text-[#0F392B]" />
                  </div>
                  <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#F9F9F7] border-t border-[#E2E2E2]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Start Your Free Trial Today
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
            7 days of full access to every feature. No credit card required. See why students who use our platform submit stronger, more organized applications.
          </p>
          <Link to="/sign-up" onClick={() => analytics.ctaPlatformStart('platform-bottom')}>
            <Button size="lg" className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-12 px-10 text-sm font-semibold shadow-lg" data-testid="portal-bottom-cta">
              Start Your 7-Day Free Trial <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground mt-3">Then $99/year. Cancel anytime.</p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
